import { useEffect } from 'react';

/**
 * Lightweight SEO hook — sets document title, meta description,
 * and OG/Twitter meta tags for the current route.
 * Updates tags in place (crawlers that execute JS pick these up).
 */
export default function usePageMeta({ title, description, image, url }) {
  useEffect(() => {
    if (title) document.title = title;

    const upsert = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      } else {
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      }
    };

    if (description) {
      upsert('meta[name="description"]', { name: 'description', content: description });
      upsert('meta[property="og:description"]', { property: 'og:description', content: description });
      upsert('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    }
    if (title) {
      upsert('meta[property="og:title"]', { property: 'og:title', content: title });
      upsert('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    }
    upsert('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsert('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    if (image) {
      upsert('meta[property="og:image"]', { property: 'og:image', content: image });
      upsert('meta[name="twitter:image"]', { name: 'twitter:image', content: image });
    }
    const canonical = url || window.location.href.split('?')[0];
    upsert('meta[property="og:url"]', { property: 'og:url', content: canonical });

    // Canonical link
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);
  }, [title, description, image, url]);
}
